import RoadmapService, { RoadmapItem } from '@/services/RoadmapService';
import { logError, logInfo } from '@/utils/logger';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
interface RoadmapContextType {
  roadmapItems: RoadmapItem[];
  isLoading: boolean;
  error: Error | null;
  refreshRoadmapItems: () => void;
}
const RoadmapContext = createContext<RoadmapContextType | undefined>(undefined);
export const useRoadmap = () => {
  const context = useContext(RoadmapContext);
  if (!context) {
    throw new Error('useRoadmap must be used within a RoadmapProvider');
  }
  return context;
};
interface RoadmapProviderProps {
  children: ReactNode;
}
export const RoadmapProvider: React.FC<RoadmapProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const {
    data: roadmapItems = [],
    error,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<RoadmapItem[], Error>({
    queryKey: ['roadmapItems'],
    queryFn: () => RoadmapService.fetchRoadmapItems(),
    staleTime: 6 * 60 * 60 * 1000,
    retry: 2,
  });
  if (error) {
    logError(error, 'RoadmapProvider - useQuery failed');
  }
  const value = useMemo(
    () => ({
      roadmapItems: roadmapItems || [],
      isLoading,
      error: error || null,
      refreshRoadmapItems: refetch,
    }),
    [roadmapItems, isLoading, error, refetch],
  );
  logInfo('RoadmapProvider render', 'RoadmapProvider', {
    isLoading,
    isFetching,
    itemCount: roadmapItems.length,
    hasError: !!error,
  });
  return (
    <RoadmapContext.Provider value={value}>{children}</RoadmapContext.Provider>
  );
};
export default RoadmapProvider;
